import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

export interface FeedItem {
  title: string;
  url: string;
  publishedAt: string | null;
  description: string | null;
}

export interface FeedResult {
  feedTitle: string;
  items: FeedItem[];
}

export type ItemStatus = 'none' | 'assessed' | 'ignored' | 'flagged';

export interface FeedDef {
  label: string;
  url: string;
  /** When set, the backend uses a CMS-specific strategy instead of plain RSS. */
  cmsType?: 'wordpress' | 'saharareporters';
}

export const DEFAULT_FEEDS: FeedDef[] = [
  { label: 'BBC News', url: 'https://feeds.bbci.co.uk/news/rss.xml' },
  { label: 'DW', url: 'https://rss.dw.com/rdf/rss-en-top' },
  { label: 'Al Jazeera', url: 'https://www.aljazeera.com/xml/rss/all.xml' },
  { label: 'The Guardian', url: 'https://www.theguardian.com/world/rss' },
  { label: 'Africa News', url: 'https://www.africanews.com/feed/rss' },
  { label: 'Channels TV', url: 'https://news.google.com/rss/search?q=site:channelstv.com&hl=en-NG&gl=NG&ceid=NG:en' },
  { label: 'NTA News', url: 'https://news.google.com/rss/search?q=site:nta.ng&hl=en-NG&gl=NG&ceid=NG:en' },
  { label: 'Punch', url: 'https://punchng.com/feed/', cmsType: 'wordpress' },
  { label: 'Vanguard', url: 'https://www.vanguardngr.com/feed/' },
  { label: 'Premium Times', url: 'https://www.premiumtimesng.com/feed/', cmsType: 'wordpress' },
  { label: 'The Guardian NG', url: 'https://news.google.com/rss/search?q=site:guardian.ng&hl=en-NG&gl=NG&ceid=NG:en' },
  { label: 'ThisDay', url: 'https://www.thisdaylive.com/index.php/feed/', cmsType: 'wordpress' },
  { label: 'Daily Trust', url: 'https://dailytrust.com/feed/', cmsType: 'wordpress' },
  { label: 'Business Day', url: 'https://businessday.ng/feed/', cmsType: 'wordpress' },
  { label: 'The Nation', url: 'https://news.google.com/rss/search?q=site:thenationonlineng.net&hl=en-NG&gl=NG&ceid=NG:en' },
  { label: 'Sahara Reporters', url: 'https://saharareporters.com/articles/rss-feed', cmsType: 'saharareporters' },
];

const STATUSES_KEY = ['news-compare-statuses'] as const;

export function useFeed(url: string | null, cmsType?: string) {
  return useQuery({
    queryKey: ['news-compare', url, cmsType],
    queryFn: () => api.get<FeedResult>('/admin/news-compare', { url, ...(cmsType ? { cmsType } : {}) }),
    enabled: !!url,
    staleTime: 2 * 60_000,
    retry: 1,
  });
}

export function useCombinedFeeds(feeds: Pick<FeedDef, 'url' | 'cmsType'>[]) {
  return useQueries({
    queries: feeds.map(({ url, cmsType }) => ({
      queryKey: ['news-compare', url, cmsType] as const,
      queryFn: () => api.get<FeedResult>('/admin/news-compare', { url, ...(cmsType ? { cmsType } : {}) }),
      staleTime: 2 * 60_000,
      retry: 1,
    })),
  });
}

export function useItemStatuses() {
  return useQuery({
    queryKey: STATUSES_KEY,
    queryFn: () => api.get<Record<string, ItemStatus>>('/admin/news-compare/statuses'),
    staleTime: 5 * 60_000,
  });
}

export function useSetItemStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ url, status }: { url: string; status: ItemStatus }) =>
      api.put('/admin/news-compare/statuses', { url, status }),
    onMutate: async ({ url, status }) => {
      await qc.cancelQueries({ queryKey: STATUSES_KEY });
      const prev = qc.getQueryData<Record<string, ItemStatus>>(STATUSES_KEY) ?? {};
      const next = { ...prev };
      if (status === 'none') delete next[url];
      else next[url] = status;
      qc.setQueryData(STATUSES_KEY, next);
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(STATUSES_KEY, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: STATUSES_KEY }),
  });
}
